import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { accessRequestApi, type MyAccessRequestStatus } from '@/services/api/accessRequest'
import { MultiSelectCombobox } from '@/components/ui/multi-select-combobox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Loader2, User, Mail, Database, FileText, CheckCircle2, Clock, Send } from 'lucide-react'

export function AccessRequestForm() {
  const { user, isAuthenticated } = useAuth()
  const [selectedKBs, setSelectedKBs] = useState<string[]>([])
  const [reason, setReason] = useState('')

  // Fetch available knowledge bases (only when authenticated)
  const { data: knowledgeBases, isLoading: kbsLoading } = useQuery({
    queryKey: ['kbs-for-request'],
    queryFn: accessRequestApi.getKnowledgeBasesForRequest,
    enabled: isAuthenticated,
  })

  // Fetch user's pending request status (only when authenticated)
  const {
    data: requestStatus,
    isLoading: statusLoading,
    refetch: refetchStatus,
  } = useQuery<MyAccessRequestStatus>({
    queryKey: ['my-request-status'],
    queryFn: accessRequestApi.getMyRequestStatus,
    enabled: isAuthenticated,
  })

  // Submit access request mutation
  const submitMutation = useMutation({
    mutationFn: (data: { requested_kbs: string[]; reason: string }) =>
      accessRequestApi.submitRequest(data),
    onSuccess: () => {
      refetchStatus()
      setSelectedKBs([])
      setReason('')
    },
  })

  // Extract user info from MSAL account
  const userName = user?.name || 'Unknown User'
  const userEmail = user?.username || 'unknown@email.com'

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (selectedKBs.length === 0) return

    submitMutation.mutate({
      requested_kbs: selectedKBs,
      reason: reason.trim() || 'No reason provided',
    })
  }

  const isLoading = kbsLoading || statusLoading

  // If user has a pending request, show pending status
  if (requestStatus?.has_pending_request && requestStatus.pending_request) {
    const pendingRequest = requestStatus.pending_request
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
              <Clock className="h-8 w-8 text-amber-600" />
            </div>
            <CardTitle className="text-xl">Request Pending</CardTitle>
            <CardDescription>
              Your access request has been submitted and is awaiting approval
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium">{pendingRequest.user_name}</p>
                  <p className="text-xs text-muted-foreground">{pendingRequest.user_email}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Database className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Requested Knowledge Bases</p>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {pendingRequest.requested_kbs.map((kb) => (
                      <Badge key={kb} variant="secondary" className="text-xs">
                        {kb}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              {pendingRequest.reason && (
                <div className="flex items-start gap-3">
                  <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Reason</p>
                    <p className="text-sm text-muted-foreground">{pendingRequest.reason}</p>
                  </div>
                </div>
              )}

              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  Submitted on{' '}
                  {new Date(pendingRequest.requested_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </div>

            <p className="text-sm text-center text-muted-foreground">
              An administrator will review your request shortly. You'll gain access once approved.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Success state after submission
  if (submitMutation.isSuccess) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-xl">Request Submitted!</CardTitle>
            <CardDescription>
              Your access request has been sent to the administrators
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-center text-muted-foreground">
              You will be notified once your request is approved. Please refresh the page to check your status.
            </p>
            <Button
              className="w-full mt-4"
              variant="outline"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-[400px] p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-xl">Request Access</CardTitle>
          <CardDescription>
            Select the knowledge bases you need access to
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* User Info - Auto-populated from SSO */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">{userName}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    {userEmail}
                  </p>
                </div>
              </div>
            </div>

            {/* Knowledge Base Selection */}
            <div className="space-y-2">
              <Label htmlFor="knowledge-bases" className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                Knowledge Bases <span className="text-destructive">*</span>
              </Label>
              {isLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <MultiSelectCombobox
                  options={knowledgeBases || []}
                  selected={selectedKBs}
                  onChange={setSelectedKBs}
                  placeholder="Select knowledge bases..."
                  searchPlaceholder="Search knowledge bases..."
                  emptyMessage="No knowledge bases found."
                  maxDisplayedTags={2}
                />
              )}
              <p className="text-xs text-muted-foreground">
                Select the knowledge bases you need to work with
              </p>
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label htmlFor="reason" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Reason (Optional)
              </Label>
              <Input
                id="reason"
                placeholder="Briefly explain why you need access..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground">
                Provide context to help administrators process your request faster
              </p>
            </div>

            {/* Error Message */}
            {submitMutation.isError && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
                <p className="text-sm text-destructive">
                  Failed to submit request. Please try again.
                </p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              disabled={selectedKBs.length === 0 || submitMutation.isPending}
            >
              {submitMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
